import { AfterViewChecked, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import orderBy from 'lodash/orderBy';
import sortBy from 'lodash/sortBy';
import { combineLatest, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { AccessPermission, Permission } from '../../models/permission.model';
import { User } from '../../models/user.model';
import { replayValue } from '../../observable.utils';
import { ProjectsService } from '../../../projects/projects.service';
import { ContainerAccess } from '../../../shared/enums/container-access.enum';
import { SelectOption } from '../../../shared/form.utils';

export enum AccessType {
  Access = 'access',
  RoleIds = 'role_ids',
}

export interface UserPermission extends Omit<Permission, '_id'> {
  user: User
  label: string
}

export type Propagatable = { propagate?: boolean }

@Component({
  host: { 'class': 'fw-container-permissions' },
  selector: 'fw-container-permissions',
  templateUrl: './fw-container-permissions.component.html',
  styleUrls: ['./fw-container-permissions.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class FwContainerPermissionsComponent implements OnChanges, OnDestroy, AfterViewChecked {
  @Input() accessType: AccessType | string = AccessType.RoleIds
  @Input() editable = false
  @Input() managedByLdap = false
  @Input() permissionOptions: SelectOption[] = []
  @Input() permissions: Permission[] = []
  @Input('loading') permissionsLoading = false
  @Input() showPropagate = false
  @Input() title?: string

  @Output() afterAdd = new EventEmitter<Permission & Propagatable>()
  @Output() afterDelete = new EventEmitter<User>()
  @Output() afterUpdate = new EventEmitter<Permission>()

  @ViewChild(MatPaginator) paginator: MatPaginator

  readonly addPermission = new FormGroup({
    user: new FormControl('', [Validators.required]),
    roles: new FormControl([] as string[], [Validators.required]),
    access: new FormControl('' as ContainerAccess, [Validators.required]),
    propagate: new FormControl(false),
  })
  columns: string[]
  readonly dataSource = new MatTableDataSource<UserPermission>([])
  eligibleUsers: (SelectOption & User)[] = []
  readonly filterFields = new FormGroup({
    name: new FormControl(''),
    email: new FormControl(''),
    roles: new FormControl(''),
    access: new FormControl(''),
  })
  usersLoading = true

  private subscriptions = {
    users: Subscription.EMPTY,
  }
  private users: Record<string, User> = {}

  constructor(private projectsService: ProjectsService) {
    combineLatest([
      replayValue(this.filterFields.controls.name),
      replayValue(this.filterFields.controls.email),
      replayValue(this.filterFields.controls.roles),
      replayValue(this.filterFields.controls.access),
    ]).pipe(
      debounceTime(200),
    ).subscribe(([name, email, roles, access]) => {
      if (this.paginator?.pageIndex > 0) {
        this.paginator.pageIndex = 0;
      }
      this.dataSource.filter = JSON.stringify({ name, email, roles, access });
    });
    this.dataSource.filterPredicate = (data: UserPermission, filter: string) => {
      const filterCols = JSON.parse(filter);
      return data.user.email.toLowerCase().includes(filterCols.email.toLowerCase()) &&
        `${data.user.firstname} ${data.user.lastname}`.toLowerCase().includes(filterCols.name.toLowerCase()) &&
        data.label.toLowerCase().includes(filterCols[this.isAccess ? 'access' : 'roles'].toLowerCase());
    };
  }

  ngAfterViewChecked(): void {
    if (!this.dataSource.paginator) {
      this.dataSource.paginator = this.paginator;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const setAccessType = (): void => {
      if (this.accessType === AccessType.Access) {
        this.columns = ['name', 'email', 'access', 'delete'];
        this.addPermission.get('access').enable();
        this.addPermission.get('roles').disable();
      } else {
        this.columns = ['name', 'email', 'roles', 'delete'];
        this.addPermission.get('access').disable();
        this.addPermission.get('roles').enable();
      }
    }

    if (changes.accessType || !this.columns) {
      setAccessType();
    }

    if (changes.managedByLdap) {
      if (this.managedByLdap) {
        this.addPermission.disable();
      } else {
        this.addPermission.enable();
        // tweak access/role disabled state after group state cascade
        setAccessType();
      }
    }

    if (changes.permissions || changes.permissionOptions) {
      this.usersLoading = true;
      this.subscriptions.users.unsubscribe();
      this.subscriptions.users = this.projectsService.getUsers().subscribe(users => {
        this.users = Object.assign({}, ...users.map(user => ({ [user._id]: user })));
        const assignedUserIds = new Set((this.permissions ?? []).map(permission => permission._id));
        const userOptions = users
          .filter(user => !user.disabled && !assignedUserIds.has(user._id))
          .map(user => ({ ...user, value: user._id, label: `${user.firstname} ${user.lastname}` }));
        this.eligibleUsers = sortBy(userOptions, 'lastname');

        const permissionLabels = Object.assign({}, ...(this.permissionOptions ?? []).map(perm => ({ [perm.value]: perm.label })));
        const usersPermissions: UserPermission[] = (this.permissions ?? []).map(permission => {
          return {
            ...permission,
            user: this.users[permission._id],
            label: isAccessPermission(permission, this.isAccess)
              ? permissionLabels[permission.access] || permission.access
              : permission.role_ids.map(roleId => permissionLabels[roleId] || roleId).join(', '),
          };
        });
        this.dataSource.data = orderBy(usersPermissions, 'user.lastname');
        this.usersLoading = false;
      });
    }
  }

  ngOnDestroy(): void {
    for (const subscription of Object.values(this.subscriptions)) {
      subscription.unsubscribe();
    }
  }

  get isAccess(): boolean {
    return this.accessType === AccessType.Access;
  }

  get loading(): boolean {
    return this.permissionsLoading || this.usersLoading;
  }

  add(): void {
    const { user, access, roles, propagate } = this.addPermission.value;
    if (this.isAccess) {
      this.afterAdd.emit({ _id: user, access });
    } else {
      this.afterAdd.emit({
        _id: user,
        role_ids: roles,
        propagate,
      });
    }
    this.addPermission.reset({
      user: '',
      roles: { value: [], disabled: this.addPermission.get('roles').disabled },
      access: { value: '', disabled: this.addPermission.get('access').disabled },
      propagate,
    } as unknown); // NG is being weird about the { value, disabled } type here
  }

  update(user: User, value: ContainerAccess | string[], opening: boolean): void {
    if (opening) {
      return;
    }
    if (this.isAccess) {
      this.afterUpdate.emit({ _id: user._id, access: value as ContainerAccess });
    } else {
      this.afterUpdate.emit({ _id: user._id, role_ids: value as string[] });
    }
  }

  delete(user: User): void {
    this.afterDelete.emit(user);
  }
}

function isAccessPermission(permission: Permission, isAccess: boolean): permission is AccessPermission {
  return isAccess; // could check `'access' in permission`, but align to component configuration
}
